import React, { useState, useRef, useEffect } from 'react';

import { formatFigure } from 'Helpers/formatService';

import TextButton from 'CommonComponents/TextButton';

import ArrowUturnLeftIcon from 'Assets/icons/arrow-uturn-left.svg';
import EditIcon from 'Assets/icons/edit.svg';

function OverridableCell({ value, originalValue, isOverridden, onSave, onRevert, readOnly = false }) {
    const [editing, setEditing] = useState(false);
    const [inputVal, setInputVal] = useState('');
    const [showPopover, setShowPopover] = useState(false);
    const inputRef = useRef(null);
    const cellRef = useRef(null);
    const hideTimeoutRef = useRef(null);

    const displayValue = value !== null && value !== undefined ? Number(value) : 0;

    const startEditing = () => {
        if (readOnly) return;
        setInputVal(String(displayValue));
        setEditing(true);
        setShowPopover(false);
    };

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commitEdit = () => {
        const parsed = parseFloat(inputVal);
        // Only save when the user actually changed the value. Reverting is an explicit
        // action via the Revert button — never inferred from exiting edit mode unchanged.
        if (!isNaN(parsed) && parsed !== displayValue) {
            onSave(parsed);
        }
        setEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setEditing(false);
        }
    };

    const handleMouseEnter = () => {
        if (readOnly || editing) return;
        clearTimeout(hideTimeoutRef.current);
        setShowPopover(true);
    };

    const handleMouseLeave = () => {
        hideTimeoutRef.current = setTimeout(() => setShowPopover(false), 50);
    };

    const handleRevert = (e) => {
        e.stopPropagation();
        onRevert();
        setShowPopover(false);
    };

    useEffect(() => {
        return () => clearTimeout(hideTimeoutRef.current);
    }, []);

    if (editing) {
        return (
            <td className={`inbud-numeric-cell tw-p-0 ${isOverridden ? '!tw-bg-amber-50' : ''}`}>
                <input
                    ref={inputRef}
                    type="number"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={handleKeyDown}
                    className="tw-w-full tw-text-right tw-bg-white tw-border tw-border-blue tw-rounded-lg tw-px-1 tw-py-0 tw-text-sm tw-text-charcoal tw-outline-none"
                    style={{ minWidth: '60px' }}
                />
            </td>
        );
    }

    return (
        <td
            ref={cellRef}
            className={`inbud-numeric-cell tw-relative tw-cursor-pointer tw-select-none ${isOverridden ? '!tw-bg-amber-50' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={startEditing}
        >
            <span className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                {isOverridden && <span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-amber-400 tw-shrink-0" title={$t('Overridden')} />}
                <span>{formatFigure(value, (v) => String(Number(v)), { precision: 2 })}</span>
            </span>

            {showPopover && !readOnly && (
                <div
                    className="tw-absolute tw-z-[999] tw-bg-white tw-rounded-lg tw-border-2 tw-border-charcoal-50 tw-p-3 tw-opacity-90 tw-w-max tw-text-left tw-top-0 tw-left-full tw-ml-1"
                    onMouseEnter={() => clearTimeout(hideTimeoutRef.current)}
                    onMouseLeave={handleMouseLeave}
                >
                    {isOverridden && (
                        <div className="tw-mb-2 tw-text-xs tw-text-charcoal-75">
                            {$t('Original value')}: <span className="tw-font-semibold">{originalValue}</span>
                        </div>
                    )}
                    <div className="tw-flex tw-items-center tw-gap-x-3">
                        <TextButton
                            variant="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowPopover(false);
                                startEditing();
                            }}
                        >
                            <EditIcon className="tw-w-3 tw-h-3" /> {$t('Edit')}
                        </TextButton>
                        {isOverridden && (
                            <TextButton variant="danger" onClick={handleRevert}>
                                <ArrowUturnLeftIcon className="tw-w-3 tw-h-3" /> {$t('Revert')}
                            </TextButton>
                        )}
                    </div>
                </div>
            )}
        </td>
    );
}

export default OverridableCell;
